import { format } from 'date-fns'
import {
  GeckoAssetModelType,
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import { installationDateFormField } from 'types/assetModel/commonFields'
import {
  assetModelFormFields,
  GeckoAssetModel,
} from 'types/entries/geckoAssetModel'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import {
  RolodexEntryJson,
  RolodexEntryPutJson,
} from 'types/rolodex/rolodexEntries'
import { parseDate, parseNumber, parseString } from 'utils/parsingHelpers'
import { optionalDateStringWithRegex } from 'utils/zodHelpers'

import { BaseAssetModelData } from './baseAssetModelData'
import { EllipsoidalHeadAssetModelJSON } from './components/pressureVessel/geckoEllipsoidalHead'
import { HemisphericalHeadAssetModelJSON } from './components/pressureVessel/geckoHemisphericalHead'
import { PressureVesselShellAssetModelJSON } from './components/pressureVessel/geckoPressureVesselShell'
import { SphereFlatCapAssetModelJSON } from './components/pressureVessel/geckoSphereFlatCap'
import { TorisphericalHeadAssetModelJSON } from './components/pressureVessel/geckoTorisphericalHead'
import { GeckoPlateJSON } from './features/geckoPlate'
import {
  convertPlatesToPolygons,
  convertPolygonsToWeldMap,
  WeldedPolygon,
  WrappingDirection,
} from './generators/weldGenerator'

export type GeckoPressureVesselAssetModelJSON = {
  name: string
  type: GeckoAssetModelType.PressureVessel
  installation_date: string | undefined
  components: Record<
    string,
    | HemisphericalHeadAssetModelJSON
    | TorisphericalHeadAssetModelJSON
    | EllipsoidalHeadAssetModelJSON
    | PressureVesselShellAssetModelJSON
    | SphereFlatCapAssetModelJSON
  >
  effective_date: string
  compass_orientation: number | undefined
  plant_north_compass_orientation: number | undefined
}

export const PressureVesselAvailableComponents: GeckoComponentModelType[] = [
  GeckoComponentModelType.PressureVesselShell,
  GeckoComponentModelType.HemisphericalHead,
  GeckoComponentModelType.TorisphericalHead,
  GeckoComponentModelType.EllipsoidalHead,
  GeckoComponentModelType.SphereFlatCap,
]

export const NewPressureVesselAssetModelJSON = {
  name: 'New Pressure Vessel',
  type: GeckoAssetModelType.PressureVessel,
  components: {},
  effective_date: format(Date.now(), 'yyyy-MM-dd'),
} as GeckoPressureVesselAssetModelJSON

export class GeckoPressureVesselAssetModel extends GeckoAssetModel<BaseAssetModelData> {
  private constructor(json: RolodexEntryJson) {
    super(json)
  }

  public static CreateFromJSON(
    json: RolodexEntryJson
  ): GeckoPressureVesselAssetModel {
    return new GeckoPressureVesselAssetModel(json)
  }

  public asJSON(): GeckoPressureVesselAssetModelJSON {
    return generatePressureVesselAssetModel(
      this.data.name,
      this.data.installation_date,
      this.raw.data
        .components as GeckoPressureVesselAssetModelJSON['components'],
      this.data.effective_date,
      this.data.compass_orientation,
      this.data.plant_north_compass_orientation
    )
  }
}

export function generatePressureVesselAssetModel(
  name: string,
  installation_date: Date | undefined,
  components: Record<
    string,
    | HemisphericalHeadAssetModelJSON
    | TorisphericalHeadAssetModelJSON
    | EllipsoidalHeadAssetModelJSON
    | PressureVesselShellAssetModelJSON
    | SphereFlatCapAssetModelJSON
  >,
  effective_date: Date,
  compass_orientation: number | undefined,
  plant_north_compass_orientation: number | undefined
): GeckoPressureVesselAssetModelJSON {
  const pressureVesselInstallationDate = installation_date
    ? installation_date.toISOString().split('T')[0]
    : undefined

  const pressureVesselEffectiveDate = effective_date.toISOString().split('T')[0]
  return {
    name: name,
    type: GeckoAssetModelType.PressureVessel,
    installation_date: pressureVesselInstallationDate,
    components: components,
    effective_date: pressureVesselEffectiveDate,
    compass_orientation: compass_orientation,
    plant_north_compass_orientation: plant_north_compass_orientation,
  }
}

export const PressureVesselAssetModelFormFields = (
  assetModelJSON: GeckoPressureVesselAssetModelJSON
) => {
  return assetModelFormFields(assetModelJSON)
}

export const PressureVesselJSONFromFormFields = (
  assetModelJSON: GeckoPressureVesselAssetModelJSON,
  formFields: FormField[]
) => {
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )
  return generatePressureVesselAssetModel(
    parseString(getFormFieldByDataKey(formFields, 'name')?.value) ?? '',
    installationDate?.value
      ? new Date(parseString(installationDate?.value) ?? '')
      : undefined,
    assetModelJSON.components,
    parseDate(assetModelJSON.effective_date) as Date,
    parseNumber(
      getFormFieldByDataKey(formFields, 'compass_orientation')?.value
    ),
    parseNumber(
      getFormFieldByDataKey(formFields, 'plant_north_compass_orientation')
        ?.value
    )
  )
}

export const pressureVesselModelFormFields = (
  assetModelJSON: GeckoPressureVesselAssetModelJSON
) => {
  const pressureVesselFormFields: FormField[] = [
    installationDateFormField(assetModelJSON.installation_date, false),
    {
      dataKey: 'effective_date',
      label: 'Effective Date',
      value: assetModelJSON.effective_date,
      unit_magnitude: null,
      validationRule: optionalDateStringWithRegex,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Date,
    },
  ]
  return pressureVesselFormFields
}

export function regeneratePressureVesselAssetModel(
  assetModel: GeckoAssetModel
): RolodexEntryPutJson {
  const rawAssetModel = assetModel.raw
  if (!rawAssetModel.data.components) {
    throw new Error(
      'Unable to regenerate this Asset Model! Components seem to be undefined.'
    )
  }
  const regeneratedComponents = {} as Record<string, unknown>
  for (const [componentID, component] of Object.entries(
    rawAssetModel.data.components
  )) {
    switch (component.type) {
      case GeckoComponentModelType.HemisphericalHead:
        regeneratedComponents[componentID] =
          regenerateHemisphericalHeadComponent(component)
        break
      case GeckoComponentModelType.TorisphericalHead:
        regeneratedComponents[componentID] =
          regenerateTorisphericalHeadComponent(component)
        break
      case GeckoComponentModelType.EllipsoidalHead:
        regeneratedComponents[componentID] =
          regenerateEllipsoidalHeadComponent(component)
        break
      case GeckoComponentModelType.PressureVesselShell:
        regeneratedComponents[componentID] =
          regeneratePressureVesselShellComponent(component)
        break
      case GeckoComponentModelType.SphereFlatCap:
        regeneratedComponents[componentID] =
          regenerateSphereFlatCapComponent(component)
        break
      default:
        throw new Error(
          'Unable to determine this type of Pressure Vessel Component!'
        )
    }
  }

  assetModel.raw.data.components = regeneratedComponents
  return assetModel.raw
}

export function regenerateHemisphericalHeadComponent(
  hemisphericalHead: Record<string, unknown>
): Record<string, unknown> {
  return hemisphericalHead
}

export function regenerateTorisphericalHeadComponent(
  torisphericalHead: Record<string, unknown>
): Record<string, unknown> {
  return torisphericalHead
}

export function regenerateEllipsoidalHeadComponent(
  ellipsoidalHead: Record<string, unknown>
): Record<string, unknown> {
  return ellipsoidalHead
}

export function regeneratePressureVesselShellComponent(
  pressureVesselShell: Record<string, unknown>
): Record<string, unknown> {
  const shellFeatures =
    pressureVesselShell.features as PressureVesselShellAssetModelJSON['features']

  // If there are no features on the asset model, there's nothing to regenerate
  if (Object.keys(shellFeatures).length === 0) {
    return pressureVesselShell
  }

  const shellPlates = new Map<string, GeckoPlateJSON>()
  const shellOther = new Map<
    string,
    Exclude<
      PressureVesselShellAssetModelJSON['features'][string],
      GeckoPlateJSON
    >
  >()

  for (const [featureID, feature] of Object.entries(shellFeatures)) {
    if (!feature.type) {
      console.error(
        'Unable to regenerate some features, as feature.type is missing!'
      )
      continue
    }

    switch (feature.type) {
      case GeckoFeatureModelType.Plate:
        shellPlates.set(featureID, feature as GeckoPlateJSON)
        break
      case GeckoFeatureModelType.Manway:
      case GeckoFeatureModelType.Nozzle:
      case GeckoFeatureModelType.PipeSeamWeld:
      case GeckoFeatureModelType.PipeCircumferentialWeld:
        shellOther.set(featureID, feature)
        break
      case GeckoFeatureModelType.VerticalWeld:
      case GeckoFeatureModelType.HorizontalWeld:
        continue
      default:
        shellOther.set(featureID, feature)
    }
  }

  const componentBoundsJSON = pressureVesselShell.component_bounds as Record<
    string,
    number
  >

  const componentBounds = GeckoComponentRect.CreateFromXYs(
    pressureVesselShell.id as string,
    componentBoundsJSON.x_start as number,
    componentBoundsJSON.y_start as number,
    componentBoundsJSON.x_end as number,
    componentBoundsJSON.y_end as number,
    GeckoMeasurementUnit.Meters,
    GeckoMeasurementUnit.Meters
  )

  const wrappingDirection = WrappingDirection.X
  const polygonArray: Array<WeldedPolygon> = []

  convertPlatesToPolygons(
    [...shellPlates.values()],
    componentBounds,
    wrappingDirection,
    polygonArray
  )

  const welds = convertPolygonsToWeldMap(
    polygonArray,
    componentBounds,
    wrappingDirection
  )

  const features = new Map<
    string,
    PressureVesselShellAssetModelJSON['features'][string]
  >([...shellPlates.entries(), ...shellOther.entries(), ...welds.entries()])

  pressureVesselShell.features = Object.fromEntries(
    features
  ) as PressureVesselShellAssetModelJSON['features']
  return pressureVesselShell
}

export function regenerateSphereFlatCapComponent(
  sphereFlatCap: Record<string, unknown>
): Record<string, unknown> {
  const capFeatures =
    sphereFlatCap.features as SphereFlatCapAssetModelJSON['features']

  // If there are no features on the asset model, there's nothing to regenerate
  if (Object.keys(capFeatures).length === 0) {
    return sphereFlatCap
  }

  const capPlates = new Map<string, GeckoPlateJSON>()
  const capOther = new Map<
    string,
    Exclude<SphereFlatCapAssetModelJSON['features'][string], GeckoPlateJSON>
  >()

  for (const [featureID, feature] of Object.entries(capFeatures)) {
    if (!feature.type) {
      console.error(
        'Unable to regenerate some features, as feature.type is missing!'
      )
      continue
    }

    switch (feature.type) {
      case GeckoFeatureModelType.Plate:
        capPlates.set(featureID, feature as GeckoPlateJSON)
        break
      case GeckoFeatureModelType.Manway:
      case GeckoFeatureModelType.Nozzle:
      case GeckoFeatureModelType.PolygonalPlate:
        capOther.set(featureID, feature)
        break
      case GeckoFeatureModelType.VerticalWeld:
      case GeckoFeatureModelType.HorizontalWeld:
        continue
      default:
        capOther.set(featureID, feature)
    }
  }

  const componentBoundsJSON = sphereFlatCap.component_bounds as Record<
    string,
    number
  >

  const componentBounds = GeckoComponentRect.CreateFromXYs(
    sphereFlatCap.id as string,
    componentBoundsJSON.x_start as number,
    componentBoundsJSON.y_start as number,
    componentBoundsJSON.x_end as number,
    componentBoundsJSON.y_end as number,
    GeckoMeasurementUnit.Meters,
    GeckoMeasurementUnit.Meters
  )

  const wrappingDirection = WrappingDirection.X
  const polygonArray: Array<WeldedPolygon> = []

  convertPlatesToPolygons(
    [...capPlates.values()],
    componentBounds,
    wrappingDirection,
    polygonArray
  )

  const welds = convertPolygonsToWeldMap(
    polygonArray,
    componentBounds,
    wrappingDirection
  )

  const features = new Map<
    string,
    SphereFlatCapAssetModelJSON['features'][string]
  >([...capPlates.entries(), ...capOther.entries(), ...welds.entries()])

  sphereFlatCap.features = Object.fromEntries(
    features
  ) as SphereFlatCapAssetModelJSON['features']
  return sphereFlatCap
}
